import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { opcoesPainel, type OpcoesPainel } from "@/api/opcoesPainel";
import { hojeMaisDias } from "@/lib/format";

// relatorio-microservice, SilpiFarmaController. Lê as tabelas do
// dispensacao-microservice (mesmo schema) — ver docs/migracao/decisoes.md (D15).
export const SILPI_FARMA_PATH = "relatorio/SilpiFarma";

// O ObjectMapper do relatorio usa NON_NULL: campo nulo não vem no JSON, por isso
// os opcionais abaixo.

export interface Indicadores {
  receitasAssinadas: number;
  /** Mês atual, sem o período do dashboard. Opcional: ausente no relatorio anterior ao campo. */
  receitasAssinadasMesAtual?: number;
  receitasDispensadas: number;
  dispensacoesSemLogin: number;
  dispensacoesSemCadastro: number;
  farmaciasCadastradas: number;
  farmaciasValidadas: number;
  farmaciasPendentes: number;
}

/** Adoção do SNCR: sempre o total (e o mês atual nas receitas), sem o período do dashboard. */
export interface Sncr {
  /** Profissionais com algum lote de numeração, em qualquer status. */
  profissionaisComLote: number;
  receitasComSncr: number;
  receitasComSncrMesAtual: number;
  /** Instituições com o SNCR liberado nas configurações. */
  instituicoesSncrLiberado: number;
  /** Instituições com algum profissional que tem lote de numeração. */
  instituicoesComLote: number;
}

export interface CidadeMapa {
  codigoIbge: string;
  farmacias: number;
  dispensacoes: number;
  cnpjsDispensando: number;
}

export interface Mapa {
  cidades: CidadeMapa[];
  farmaciasSemLocalizacao: number;
  dispensacoesSemLocalizacao: number;
}

export interface CnpjSemLocalizacao {
  /** Ausente em estabelecimento público só com CNES (ddl/007 do dispensacao). */
  cnpj?: string;
  cnes?: string;
  cadastrada: boolean;
  razaoSocial?: string;
  situacaoCadastralRf?: string;
  dispensacoes: number;
  ultimaDispensacao?: string;
  nomeResponsavelTecnico?: string;
  crfResponsavelTecnico?: string;
  ufCrfResponsavelTecnico?: string;
  tentativasAutomaticas: number;
}

export type OrigemLocalizacao = "MANUAL" | "CEP_CADASTRO" | "AUTOMATICA_RF" | "AUTOMATICA_CNES";

export interface CnpjLocalizado {
  /** Ausente em estabelecimento público só com CNES. */
  cnpj?: string;
  cnes?: string;
  codigoIbge: string;
  razaoSocial?: string;
  /** Origem que vale para o mapa. Precedência: MANUAL > CEP_CADASTRO > AUTOMATICA_RF/AUTOMATICA_CNES. */
  origem: OrigemLocalizacao;
  situacaoCadastralRf?: string;
  situacaoAnvisa?: string;
  observacao?: string;
  dataHoraAlteracao?: string;
  dispensacoes: number;
}

export interface FarmaciaPendente {
  id: number;
  /** Ausente em estabelecimento público sem CNPJ. */
  cnpj?: string;
  publica?: boolean;
  cnes?: string;
  razaoSocial: string;
  nomeFantasia?: string;
  cidade?: string;
  estado?: string;
  dataCadastro?: string;
  nomeResponsavelTecnico?: string;
  emailResponsavelTecnico?: string;
  telefoneResponsavelTecnico?: string;
  crfResponsavelTecnico?: string;
  ufCrfResponsavelTecnico?: string;
  dispensacoes: number;
}

export interface LocalizacaoManual {
  codigoIbge: string;
  razaoSocial?: string;
  situacaoAnvisa?: string;
  observacao?: string;
}

// --- Período ---------------------------------------------------------------

export type Periodo = "tudo" | "12m" | "30d";

export const PERIODOS: { value: Periodo; label: string }[] = [
  { value: "tudo", label: "Todo o período" },
  { value: "12m", label: "Últimos 12 meses" },
  { value: "30d", label: "Últimos 30 dias" },
];

export function isPeriodo(value: string | null): value is Periodo {
  return PERIODOS.some((p) => p.value === value);
}

function paramsDoPeriodo(periodo: Periodo): { inicio?: string; fim?: string } {
  if (periodo === "12m") return { inicio: hojeMaisDias(-365), fim: hojeMaisDias(0) };
  if (periodo === "30d") return { inicio: hojeMaisDias(-30), fim: hojeMaisDias(0) };
  return {};
}

// --- Chamadas --------------------------------------------------------------

export async function getIndicadores(periodo: Periodo): Promise<Indicadores> {
  const { data } = await api.get<Indicadores>(`${SILPI_FARMA_PATH}/Indicadores`, { params: paramsDoPeriodo(periodo) });
  return data;
}

export async function getSncr(): Promise<Sncr> {
  const { data } = await api.get<Sncr>(`${SILPI_FARMA_PATH}/Sncr`);
  return data;
}

export async function getMapa(periodo: Periodo): Promise<Mapa> {
  const { data } = await api.get<Mapa>(`${SILPI_FARMA_PATH}/Mapa`, { params: paramsDoPeriodo(periodo) });
  return data;
}

export async function getCnpjsSemLocalizacao(): Promise<CnpjSemLocalizacao[]> {
  const { data } = await api.get<CnpjSemLocalizacao[]>(`${SILPI_FARMA_PATH}/CnpjsSemLocalizacao`);
  return data;
}

export async function getCnpjsLocalizados(): Promise<CnpjLocalizado[]> {
  const { data } = await api.get<CnpjLocalizado[]>(`${SILPI_FARMA_PATH}/CnpjsLocalizados`);
  return data;
}

export async function getFarmaciasPendentes(): Promise<FarmaciaPendente[]> {
  const { data } = await api.get<FarmaciaPendente[]>(`${SILPI_FARMA_PATH}/FarmaciasPendentes`);
  return data;
}

export async function validarFarmacia(id: number): Promise<void> {
  await api.put(`${SILPI_FARMA_PATH}/Farmacia/${id}/Validar`);
}

/** Estabelecimento: pelo CNPJ ou, em estabelecimento público sem CNPJ, pelo CNES. */
export interface Estabelecimento {
  cnpj?: string;
  cnes?: string;
}

export async function salvarLocalizacaoManual(chave: Estabelecimento, body: LocalizacaoManual): Promise<void> {
  const caminho = chave.cnpj ? chave.cnpj : `Cnes/${chave.cnes}`;
  await api.put(`${SILPI_FARMA_PATH}/LocalizacaoManual/${caminho}`, body);
}

// --- Hooks -----------------------------------------------------------------

const KEY = ["silpi-farma"] as const;

export function useIndicadores(periodo: Periodo, opcoes: OpcoesPainel = {}) {
  return useQuery({
    queryKey: [...KEY, "indicadores", periodo],
    queryFn: () => getIndicadores(periodo),
    ...opcoesPainel(opcoes),
  });
}

export function useSncr(opcoes: OpcoesPainel = {}) {
  return useQuery({ queryKey: [...KEY, "sncr"], queryFn: getSncr, ...opcoesPainel(opcoes) });
}

export function useMapa(periodo: Periodo, opcoes: OpcoesPainel = {}) {
  return useQuery({
    queryKey: [...KEY, "mapa", periodo],
    queryFn: () => getMapa(periodo),
    ...opcoesPainel(opcoes),
  });
}

export function useCnpjsSemLocalizacao() {
  return useQuery({ queryKey: [...KEY, "sem-localizacao"], queryFn: getCnpjsSemLocalizacao });
}

export function useCnpjsLocalizados() {
  return useQuery({ queryKey: [...KEY, "localizados"], queryFn: getCnpjsLocalizados });
}

export function useFarmaciasPendentes() {
  return useQuery({ queryKey: [...KEY, "pendentes"], queryFn: getFarmaciasPendentes });
}

export function useValidarFarmacia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: validarFarmacia,
    // Lista de pendentes e o card "validadas · pendentes".
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: KEY, predicate: (q) => ["pendentes", "indicadores"].includes(String(q.queryKey[1])) }),
  });
}

export function useSalvarLocalizacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chave, body }: { chave: Estabelecimento; body: LocalizacaoManual }) =>
      salvarLocalizacaoManual(chave, body),
    // Mapa e as duas listas mudam; indicadores não.
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY, predicate: (q) => q.queryKey[1] !== "indicadores" }),
  });
}
